import { Field } from '@/components/ui/field';
import { useRegisterBrandAction } from '@/hooks/actions/use-register-brand-action';
import { useRequestBrandSignupCodeAction } from '@/hooks/actions/use-request-brand-signup-code-action';
import { metaPixel } from '@/libs/meta-pixel';
import { isWorkEmailRequiredError, type AuthError } from '@/pages/auth/_components/auth-error';
import { AuthErrorAlert } from '@/pages/auth/_components/AuthErrorAlert';
import { authFieldLabelSx } from '@/pages/auth/_components/auth-field-label';
import { Box, Button, Flex, Heading, Input, PinInput, Stack, Text } from '@chakra-ui/react';
import { useRef, useState, type ClipboardEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { signupAuthError } from './signup-errors';
import { signupLanguage, type SignupDetails } from './signup-details';
import { useResendCooldown } from './use-resend-cooldown';

const CODE_LENGTH = 6;

interface SignupCodeStepProps {
  details: SignupDetails;
  onChangeEmail: (error?: AuthError) => void;
  onCreated: () => void;
}

/**
 * The code sent to the address: given back, it creates the account. "Change
 * the address" returns to the filled form; a new code waits for the minute the
 * API enforces between two sendings.
 */
export const SignupCodeStep = ({ details, onChangeEmail, onCreated }: SignupCodeStepProps) => {
  const { t, i18n } = useTranslation();
  const registerBrand = useRegisterBrandAction();
  const requestCode = useRequestBrandSignupCodeAction();
  const { remaining, restart } = useResendCooldown();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const verifying = useRef(false);
  const lastAutomaticCode = useRef<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [resent, setResent] = useState(false);
  const translate = (key: string, fallback?: string) =>
    fallback === undefined ? t(key) : t(key, { defaultValue: fallback });

  const verify = async (code: string) => {
    if (verifying.current || requestCode.isPending) return;
    setResent(false);

    if (!/^\d{6}$/.test(code)) {
      setError({ message: t('signup.verify.errors.incomplete'), code: null });
      return;
    }

    verifying.current = true;
    setIsVerifying(true);
    setError(null);
    try {
      await registerBrand.mutateAsync({ ...details, code });
      metaPixel.track('CompleteRegistration', { content_name: 'email' });
      onCreated();
    } catch (failure) {
      verifying.current = false;
      setIsVerifying(false);
      const refusal = signupAuthError(failure, translate);
      if (isWorkEmailRequiredError(refusal.code)) onChangeEmail(refusal);
      else setError(refusal);
    }
  };

  const updateDigits = (value: string[]) => {
    if (verifying.current || requestCode.isPending) return;
    setDigits(value);
    const candidate = value.join('');
    if (/^\d{6}$/.test(candidate) && candidate !== lastAutomaticCode.current) {
      lastAutomaticCode.current = candidate;
      void verify(candidate);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void verify(digits.join(''));
  };

  const resend = async () => {
    if (verifying.current) return;
    setError(null);
    setResent(false);
    try {
      await requestCode.mutateAsync({
        email: details.email,
        language: signupLanguage(i18n.language),
      });
      restart();
      lastAutomaticCode.current = null;
      verifying.current = false;
      setIsVerifying(false);
      setDigits(Array(CODE_LENGTH).fill(''));
      setResent(true);
    } catch (failure) {
      const refusal = signupAuthError(failure, translate, 'signup.verify.errors.sendFailed');
      if (isWorkEmailRequiredError(refusal.code)) onChangeEmail(refusal);
      else setError(refusal);
    }
  };

  const fillCode = (value: string) => {
    const pasted = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    updateDigits(Array.from({ length: CODE_LENGTH }, (_, index) => pasted[index] ?? ''));
  };

  const pasteCode = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    fillCode(event.clipboardData.getData('text'));
  };

  return (
    <Stack as="form" onSubmit={submit} gap={0} width="full">
      <Heading
        as="h1"
        fontFamily="heading"
        fontSize={{ base: '26px', md: '30px' }}
        fontWeight="600"
        letterSpacing="-0.02em"
        lineHeight="1.12"
        color="ink"
      >
        {t('signup.verify.title')}
      </Heading>
      <Text fontSize="14.5px" color="inkMuted" mt="11px" lineHeight="1.5" fontWeight="300">
        {t('signup.verify.sentTo', { email: details.email })}
      </Text>
      <Text fontSize="13px" color="inkSubtle" mt="4px">
        {t('signup.verify.spamHint')}
      </Text>
      <Box mt="6px">
        <Button
          variant="plain"
          h="auto"
          minW={0}
          p={0}
          fontSize="13px"
          fontWeight="600"
          color="ink"
          textDecoration="underline"
          disabled={isVerifying || registerBrand.isPending || requestCode.isPending}
          onClick={() => onChangeEmail()}
        >
          {t('signup.verify.changeEmail')}
        </Button>
      </Box>

      <Stack gap={4} mt="24px">
        {error && <AuthErrorAlert error={error} />}
        {resent && !error && (
          <Text fontSize="13px" color="green.700">
            {t('signup.verify.resent')}
          </Text>
        )}

        <Field>
          <label
            htmlFor="signup-code"
            style={{ ...authFieldLabelSx, display: 'block', marginBottom: '7px' }}
          >
            {t('signup.verify.label')}
          </label>
          <PinInput.Root
            value={digits}
            onValueChange={({ value }) => updateDigits(value)}
            ids={{ input: (index) => (index === '0' ? 'signup-code' : `signup-code-${index}`) }}
            type="numeric"
            variant="outline"
            otp
            placeholder="0"
            disabled={isVerifying || registerBrand.isPending || requestCode.isPending}
            aria-label={t('signup.verify.label')}
          >
            <PinInput.Control
              display="grid"
              gridTemplateColumns="repeat(6, minmax(0, 1fr))"
              gap="8px"
              w="full"
            >
              <PinInput.Context>
                {(pinInput) =>
                  Array.from({ length: CODE_LENGTH }, (_, index) => {
                    const inputProps = pinInput.getInputProps({ index });
                    return (
                      <Input
                        {...inputProps}
                        size="lg"
                        key={index}
                        onPaste={pasteCode}
                        onChange={(event) => {
                          if (event.currentTarget.value.length > 2) {
                            // Autofill is a whole code, regardless of the focused box.
                            const value = event.currentTarget.value
                              .replace(/\D/g, '')
                              .slice(0, CODE_LENGTH);
                            fillCode(value);
                            event.currentTarget.value = value[index] ?? '';
                          } else {
                            inputProps.onChange?.(event);
                          }
                        }}
                        variant="outline"
                        w="full"
                        minW={0}
                        h="56px"
                        p={0}
                        border="1px solid"
                        borderColor="border"
                        rounded="12px"
                        bg="white"
                        textAlign="center"
                        fontFamily="heading"
                        fontSize="22px"
                        fontWeight="bold"
                        color="ink"
                        _focus={{ borderColor: 'ink', outline: 'none' }}
                      />
                    );
                  })
                }
              </PinInput.Context>
            </PinInput.Control>
            <PinInput.HiddenInput />
          </PinInput.Root>
        </Field>

        <Button
          type="submit"
          colorPalette="primary"
          size="lg"
          width="full"
          borderRadius="12px"
          h={12}
          loading={isVerifying || registerBrand.isPending}
          loadingText={t('signup.verify.verifying')}
        >
          {t('signup.verify.submit')}
        </Button>

        <Flex justify="center" align="baseline" gap="6px" fontSize="13px" color="inkMuted">
          <Text as="span">{t('signup.verify.resendQuestion')}</Text>
          <Button
            variant="plain"
            h="auto"
            minW={0}
            p={0}
            fontSize="13px"
            fontWeight="600"
            color="ink"
            disabled={remaining > 0 || isVerifying}
            loading={requestCode.isPending}
            onClick={resend}
          >
            {remaining > 0
              ? t('signup.verify.resendIn', { seconds: remaining })
              : t('signup.verify.resend')}
          </Button>
        </Flex>
      </Stack>
    </Stack>
  );
};
